import { createSignal, createEffect } from 'solid-js'
import { Language, translations, Translations } from './translations'

const STORAGE_KEY = 'gpt-clicker-language'

// Check if a string is a valid language
function isValidLanguage(lang: string | null): lang is Language {
  return lang === 'en' || lang === 'es' || lang === 'de' || lang === 'fr' || lang === 'pt' || lang === 'pl' || lang === 'nl' || lang === 'it' || lang === 'ja' || lang === 'ko'
}

// Get language from URL query param
function getLanguageFromURL(): Language | null {
  const params = new URLSearchParams(window.location.search)
  const lang = params.get('lang')
  return isValidLanguage(lang) ? lang : null
}

// Update URL with language param
function updateURLLanguage(lang: Language) {
  const url = new URL(window.location.href)
  url.searchParams.set('lang', lang)
  window.history.replaceState({}, '', url.toString())
}

// Get saved language or detect from browser
function getInitialLanguage(): Language {
  // Check URL first (for shared links)
  const urlLang = getLanguageFromURL()
  if (urlLang) {
    return urlLang
  }
  
  // Check localStorage
  const saved = localStorage.getItem(STORAGE_KEY)
  if (isValidLanguage(saved)) {
    return saved
  }
  
  // Detect from browser
  const browserLang = navigator.language.toLowerCase()
  if (browserLang.startsWith('es')) {
    return 'es'
  }
  if (browserLang.startsWith('de')) {
    return 'de'
  }
  if (browserLang.startsWith('fr')) {
    return 'fr'
  }
  if (browserLang.startsWith('pt')) {
    return 'pt'
  }
  if (browserLang.startsWith('pl')) {
    return 'pl'
  }
  if (browserLang.startsWith('nl')) {
    return 'nl'
  }
  if (browserLang.startsWith('it')) {
    return 'it'
  }
  if (browserLang.startsWith('ja')) {
    return 'ja'
  }
  if (browserLang.startsWith('ko')) {
    return 'ko'
  }
  
  return 'en'
}

// Create reactive language signal
const [currentLanguage, setCurrentLanguage] = createSignal<Language>(getInitialLanguage())

// Save to localStorage and update URL when language changes
createEffect(() => {
  const lang = currentLanguage()
  localStorage.setItem(STORAGE_KEY, lang)
  updateURLLanguage(lang)
})

// Get translation function
export function t(key: keyof Translations): string {
  return translations[currentLanguage()][key] || translations.en[key] || key
}

// Export language controls
export { currentLanguage, setCurrentLanguage }
export type { Language }
